import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Grid,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import { Getrequest } from "../../../context/AxiosRequest";
import { ContextData } from "../../../context/Context";
import GetAdminCredentials from "../../../context/GetAdminCredentials";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SoundAlert from "../../../routes/SoundAlert";

const MATRIX_ENDPOINT = "auth/get_matrix";

const asText = (value, fallback = "Unknown") => {
	if (value === null || value === undefined) {
		return fallback;
	}

	const text = String(value).trim();
	return text.length ? text : fallback;
};

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const getMergeKey = (item) => {
	return (
		item?.user_token ||
		item?.token ||
		item?.id ||
		item?.bio_id ||
		item?.edu_table_id ||
		item?.demography_table_id ||
		item?.bank_details_id ||
		item?.apply_loan_id ||
		item?.loan_id ||
		JSON.stringify(item)
	);
};

const getNestedField = (row, key) => {
	if (hasValue(row?.[key])) {
		return row[key];
	}

	const candidates = [
		row?.loanrequest,
		row?.loanData,
		row?.loan,
		row?.bankdetails,
		row?.bankData,
		row?.bank,
		row?.demography,
		row?.demographyData,
		row?.education,
		row?.educationData,
		row?.applicantBio,
		row?.bio,
		row?.data,
	];

	for (const container of candidates) {
		if (Array.isArray(container)) {
			for (const item of container) {
				if (hasValue(item?.[key])) {
					return item[key];
				}
			}
		}

		if (container && typeof container === "object" && hasValue(container?.[key])) {
			return container[key];
		}
	}

	return undefined;
};


const pickFirst = (source, keys, fallback = "Unknown") => {
	for (const key of keys) {
		const value = source?.[key];
		if (value !== undefined && value !== null && String(value).trim() !== "") {
			return asText(value, fallback);
		}
	}

	return fallback;
};

const normalizeRows = (response) => {
	if (Array.isArray(response)) {
		const uniqueRows = new Map();
		response.forEach((item) => {
			uniqueRows.set(getMergeKey(item), item);
		});
		return Array.from(uniqueRows.values());
	}

	const dataRoot = response?.data && typeof response.data === "object" ? response.data : null;
	const objectRoot = response && typeof response === "object" ? response : null;
	const roots = [objectRoot, dataRoot].filter(Boolean);

	for (const root of roots) {
		if (Array.isArray(root)) {
			const uniqueRows = new Map();
			root.forEach((item) => {
				uniqueRows.set(getMergeKey(item), item);
			});
			return Array.from(uniqueRows.values());
		}

		const arrayEntries = Object.entries(root).filter(([, value]) => Array.isArray(value));
		if (!arrayEntries.length) {
			continue;
		}

		const merged = new Map();
		arrayEntries.forEach(([, list]) => {
			list.forEach((item) => {
				const key = getMergeKey(item);
				const previous = merged.get(key) || {};
				merged.set(key, { ...previous, ...item });
			});
		});

		return Array.from(merged.values());
	}

	return [];
};

const countBy = (rows, getter) => {
	return rows.reduce((accumulator, row) => {
		const label = getter(row);
		accumulator[label] = (accumulator[label] || 0) + 1;
		return accumulator;
	}, {});
};

const getClientName = (row) => {
	const direct = asText(row?.clientName, "");
	if (direct) {
		return direct;
	}

	const firstName = asText(row?.fname, "").trim();
	const middleName = asText(row?.md_name, "").trim();
	const lastName = asText(row?.lname, "").trim();
	const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, " ").trim();

	return fullName || "";
};

const MATRIX_COLUMNS = [
	{
		id: "clientName",
		label: "Client Name",
		getValue: (row) => getClientName(row),
	},
	{
		id: "loan_category",
		label: "Loan Category",
		getValue: (row) => asText(getNestedField(row, "loan_category"), ""),
	},
	{
		id: "tenor",
		label: "Tenor",
		getValue: (row) => asText(getNestedField(row, "tenor"), ""),
	},
	{
		id: "mobileNumber",
		label: "Mobile Number",
		getValue: (row) => asText(row?.mobileNumber, ""),
	},
	{
		id: "gender",
		label: "Gender",
		getValue: (row) => asText(row?.gender, ""),
	},
	{
		id: "marital_status",
		label: "Marital Status",
		getValue: (row) => asText(row?.marital_status, ""),
	},
	{
		id: "d_o_b",
		label: "Date Of Birth",
		getValue: (row) => asText(row?.d_o_b, ""),
	},
	{
		id: "resdential_type",
		label: "Residential Type",
		getValue: (row) => asText(row?.resdential_type, ""),
	},
	{
		id: "matric_num",
		label: "Matric Number",
		getValue: (row) => asText(row?.matric_num, ""),
	},
	{
		id: "university",
		label: "University",
		getValue: (row) => asText(row?.university, ""),
	},
	{
		id: "loan_applied",
		label: "Loan Applied",
		getValue: (row) => asText(row?.loan_applied, ""),
	},
	{
		id: "loan_type",
		label: "Loan Type",
		getValue: (row) => asText(row?.loan_type, ""),
	},
	{
		id: "state_of_origin",
		label: "State Of Origin",
		getValue: (row) => asText(getNestedField(row, "state_of_origin"), ""),
	},
	{
		id: "local_gov",
		label: "Local Gov",
		getValue: (row) => asText(getNestedField(row, "local_gov"), ""),
	},
	{
		id: "bank_name",
		label: "Bank Name",
		getValue: (row) => asText(getNestedField(row, "bank_name"), ""),
	},
	{
		id: "account_name",
		label: "Account Name",
		getValue: (row) => asText(getNestedField(row, "account_name"), ""),
	},
	{
		id: "account_number",
		label: "Account Number",
		getValue: (row) => asText(getNestedField(row, "account_number"), ""),
	},
	{
		id: "nin_number",
		label: "NIN Number",
		getValue: (row) => asText(getNestedField(row, "nin_number"), ""),
	},
	{
		id: "bvn_number",
		label: "BVN Number",
		getValue: (row) => asText(getNestedField(row, "bvn_number"), ""),
	},
	{
		id: "tin_number",
		label: "TIN Number",
		getValue: (row) => asText(getNestedField(row, "tin_number"), ""),
	},
	{
		id: "disbursementAmount",
		label: "Disbursement Amount",
		getValue: (row) => {
			const text = asText(getNestedField(row, "disbursementAmountText"), "");
			if (!text) return "";
			// Extract figure inside parentheses, e.g., "Two Hundred Thousand Naira Only (N200,000.00)" -> "N200,000.00"
			const match = text.match(/\(([^)]+)\)/);
			return match ? match[1] : "";
		},
	},
	{
		id: "guarantor_name",
		label: "Guarantor Name",
		getValue: (row) => {
			const first_name = asText(getNestedField(row, "first_name"), "").trim();
			const middle_name = asText(getNestedField(row, "middle_name"), "").trim();
			const surname = asText(getNestedField(row, "surname"), "").trim();

			return `${first_name} ${middle_name} ${surname}`.replace(/\s+/g, " ").trim();
		},
	},
	{
		id: "phone_no_1",
		label: "Guarantor Phone Number 1",
		getValue: (row) => asText(getNestedField(row, "phone_no_1"), ""),
	},
	{
		id: "phone_no_2",
		label: "Guarantor Phone Number 2",
		getValue: (row) => asText(getNestedField(row, "phone_no_2"), ""),
	},
	{
		id: "psn_no",
		label: "Guarantor PSN",
		getValue: (row) => asText(getNestedField(row, "psn_no"), ""),
	},
	{
		id: "bvn",
		label: "BVN",
		getValue: (row) => asText(getNestedField(row, "bvn"), ""),
	},
	{
		id: "psn_ippis",
		label: "Sponsor PSN",
		getValue: (row) => asText(getNestedField(row, "psn_ippis"), ""),
	}
];

function Matrix() {


	
	const { accesstoken, GlobalLoader,UserTokens } = useContext(ContextData);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [rows, setRows] = useState([]);
	const [refreshTick, setRefreshTick] = useState(0);
	const navigate = useNavigate();
	const [filters, setFilters] = useState({
		name: "",
		phone: "",
		university: "",
		loanType: "all",
		loanCategory: "all",
		lga: "",
	});


	 
	   React.useEffect(() => {
	   
		   const fetchPersonData = async () => {
	   
				
			   GetAdminCredentials(UserTokens,accesstoken).then(res =>{
                           
                                  const isallow = res[0]?.matrix_status

								    if(!isallow){
										 
										    navigate("/verifier_loan/verification"); // Redirect to home or another appropriate page
										    toast.warn("You are not allowed to access the matrix.");
											SoundAlert("error");
											return;
									}
                                       
                                })
				  
		   } 
	   
			fetchPersonData();
		
		 }, [UserTokens]);

	useEffect(() => {
		let isMounted = true;

		async function fetchMatrixData() {
			setLoading(true);
			setError("");

			
             
		
	             const isallow = await GetAdminCredentials(UserTokens,accesstoken).then(res => res[0]?.matrix_status);
                           
                           

								   
		

			const response = await Getrequest(isallow ? MATRIX_ENDPOINT : "", accesstoken);

			if (!isMounted) {
				return;
			}

			if (response?.success === false) {
				setRows([]);
				setError(response?.error?.message || response?.error || "Unable to load analytics data.");
				setLoading(false);
				return;
			}

			setRows(normalizeRows(response));
			setLoading(false);

			
		}

		fetchMatrixData();

		return () => {
			isMounted = false;
		};
	}, [accesstoken, GlobalLoader, refreshTick]);

	const filteredRows = useMemo(() => {
		const nameQuery = filters.name.trim().toLowerCase();
		const phoneQuery = filters.phone.trim().toLowerCase();
		const universityQuery = filters.university.trim().toLowerCase();
		const lgaQuery = filters.lga.trim().toLowerCase();

		return rows.filter((row) => {
			const rowName = getClientName(row).toLowerCase();
			const rowPhone = asText(row?.mobileNumber, "").toLowerCase();
			const rowUniversity = asText(row?.university, "").toLowerCase();
			const rowLoanType = asText(row?.loan_type, "").toLowerCase();
			const rowLoanCategory = asText(getNestedField(row, "loan_category"), "").toLowerCase();
			const rowLga = asText(row?.local_gov, "").toLowerCase();

			const nameMatch = !nameQuery || rowName.includes(nameQuery);
			const phoneMatch = !phoneQuery || rowPhone.includes(phoneQuery);
			const universityMatch = !universityQuery || rowUniversity.includes(universityQuery);
			const lgaMatch = !lgaQuery || rowLga.includes(lgaQuery);
			const loanTypeMatch = filters.loanType === "all" || rowLoanType === filters.loanType.toLowerCase();
			const loanCategoryMatch = filters.loanCategory === "all" || rowLoanCategory === filters.loanCategory.toLowerCase();

			return nameMatch && phoneMatch && universityMatch && lgaMatch && loanTypeMatch && loanCategoryMatch;
		});
	}, [rows, filters]);

	const derived = useMemo(() => {
		const genderCounts = countBy(filteredRows, (row) => pickFirst(row, ["gender", "sex"]));
		const loanTypeCounts = countBy(filteredRows, (row) => pickFirst(row, ["loan_type"]));
		const stateCounts = countBy(filteredRows, (row) => pickFirst(row, ["state_of_origin"]));

		// Calculate total disbursement amount
		const totalDisbursement = filteredRows.reduce((sum, row) => {
			const text = asText(getNestedField(row, "disbursementAmountText"), "");
			if (!text) return sum;
			const match = text.match(/\(N([\d,]+)\.?\d*\)/);
			if (match) {
				const numericValue = parseFloat(match[1].replace(/,/g, ""));
				return sum + (isNaN(numericValue) ? 0 : numericValue);
			}
			return sum;
		}, 0);

		return {
			genderCounts,
			loanTypeCounts,
			stateCounts,
			totalDisbursement,
		};
	}, [filteredRows]);

	const totalApplicants = rows.length;
	const filteredApplicants = filteredRows.length;
	const totalLoanTypes = Object.keys(derived.loanTypeCounts).length;
	const totalStates = Object.keys(derived.stateCounts).length;
	const totalGenders = Object.keys(derived.genderCounts).length;
	const loanTypeOptions = useMemo(() => {
		const types = Array.from(new Set(rows.map((row) => asText(getNestedField(row, "loan_type"), "")).filter(Boolean)));
		return types.sort((a, b) => a.localeCompare(b));
	}, [rows]);

	const matrixRecords = useMemo(() => {
		return filteredRows.map((row, index) => {
			const record = {
				"S/N": index + 1,
			};

			MATRIX_COLUMNS.forEach((column) => {
				record[column.label] = column.getValue(row);
			});

			return record;
		});
	}, [filteredRows]);

	const handleExportExcel = () => {
		if (!matrixRecords.length) {
			return;
		}

		// Create worksheet with matrix records
		const worksheet = XLSX.utils.json_to_sheet(matrixRecords);
		
		// Add blank row
		XLSX.utils.sheet_add_json(worksheet, [{}], { skipHeader: true, origin: -1 });
		
		// Add total row
		const totalRow = {
			"S/N": "",
			"Client Name": "",
			"Loan Category": "",
			"Tenor": "",
			"Mobile Number": "",
			"Gender": "",
			"Marital Status": "",
			"Date Of Birth": "",
			"Residential Type": "",
			"Matric Number": "",
			"University": "",
			"Loan Applied": "",
			"Loan Type": "",
			"State Of Origin": "",
			"Local Gov": "",
			"Bank Name": "",
			"Account Name": "",
			"Account Number": "",
			"NIN Number": "",
			"BVN Number": "",
			"TIN Number": "TOTAL:",
			"Disbursement Amount": `₦${derived.totalDisbursement.toLocaleString()}`,
			"Guarantor Name": "",
			"Guarantor Phone Number 1": "",
			"Guarantor Phone Number 2": "",
			"Guarantor PSN": "",
			"BVN": "",
			"Sponsor PSN": "",
		};
		XLSX.utils.sheet_add_json(worksheet, [totalRow], { skipHeader: true, origin: -1 });

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Matrix Records");

		const dateStamp = new Date().toISOString().slice(0, 10);
		XLSX.writeFile(workbook, `matrix-records-${dateStamp}.xlsx`);
	};

	const handleFilterChange = (key) => (event) => {
		setFilters((previous) => ({
			...previous,
			[key]: event.target.value,
		}));
	};

	const clearFilters = () => {
		setFilters({
			name: "",
			phone: "",
			university: "",
			loanType: "all",
			loanCategory: "all",
			lga: "",
		});
	};

	return (
		<div className="min-h-[92vh] bg-[radial-gradient(circle_at_top_left,_#d1fae5,_#ecfccb_38%,_#f8fafc_100%)] p-6 text-slate-900">
			<div className="mx-auto flex max-w-7xl flex-col gap-6">
				<div className="rounded-3xl border border-emerald-100 bg-white/90 p-6 shadow-xl backdrop-blur">
					<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
						<div>
							<Typography variant="overline" sx={{ color: "#065f46", letterSpacing: 2, fontWeight: 700 }}>
								Bank Disbursement Matrix
							</Typography>
							<Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1, mt: 0.5, color: "#0f172a" }}>
								Matrix Records View
							</Typography>
							<Typography sx={{ color: "#334155", maxWidth: 760, mt: 1 }}>
								All applicant records are displayed using your selected object properties. You can review totals and export the full table to Excel.
							</Typography>
						</div>

						<Stack direction="row" spacing={1.2}>
							<Button
								variant="contained"
								onClick={() => setRefreshTick((value) => value + 1)}
								sx={{
									alignSelf: "flex-start",
									background: "linear-gradient(135deg, #047857 0%, #065f46 100%)",
									color: "#fff",
									px: 3,
									py: 1.2,
									borderRadius: 999,
									textTransform: "none",
									fontWeight: 700,
								}}
							>
								Refresh Data
							</Button>
							<Button
								variant="outlined"
								onClick={handleExportExcel}
								disabled={!matrixRecords.length}
								sx={{
									alignSelf: "flex-start",
									borderColor: "#065f46",
									color: "#065f46",
									px: 3,
									py: 1.2,
									borderRadius: 999,
									textTransform: "none",
									fontWeight: 700,
								}}
							>
								Export Excel
							</Button>
						</Stack>
					</div>
				</div>

				{error ? <Alert severity="error">{error}</Alert> : null}

				<Grid container spacing={2}>
					{[
						{ label: "Showing records", value: filteredApplicants },
						{ label: "Loan types", value: totalLoanTypes },
						{ label: "States represented", value: totalStates },
						{ label: "Gender groups", value: totalGenders },
						{ label: "Total Disbursement", value: `₦${derived.totalDisbursement.toLocaleString()}`, isAmount: true },
					].map((item) => (
						<Grid item xs={12} sm={6} md={4} key={item.label}>
							<Card
								sx={{
									height: "100%",
									borderRadius: 4,
									background: "rgba(255,255,255,0.95)",
									color: "#0f172a",
									border: "1px solid rgba(16,185,129,0.18)",
								}}
							>
								<CardContent>
									<Typography variant="body2" sx={{ color: "#475569" }}>
										{item.label}
									</Typography>
									<Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
										{loading ? "--" : item.isAmount ? item.value : item.value.toLocaleString()}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>

				{loading ? (
					<div className="flex min-h-[40vh] items-center justify-center rounded-3xl border border-emerald-100 bg-white/70">
						<Stack alignItems="center" spacing={2}>
							<CircularProgress sx={{ color: "#065f46" }} />
							<Typography sx={{ color: "#334155" }}>Loading matrix records...</Typography>
						</Stack>
					</div>
				) : (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Card
								sx={{
									borderRadius: 4,
									background: "rgba(255,255,255,0.95)",
								}}
							>
								<CardContent>
									<Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
										Search And Filter
									</Typography>
									<Grid container spacing={1.5}>
										<Grid item xs={12} md={2}>
											<TextField
												fullWidth
												size="small"
												label="Name"
												value={filters.name}
												onChange={handleFilterChange("name")}
											/>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												fullWidth
												size="small"
												label="Phone"
												value={filters.phone}
												onChange={handleFilterChange("phone")}
											/>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												fullWidth
												size="small"
												label="University"
												value={filters.university}
												onChange={handleFilterChange("university")}
											/>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												select
												fullWidth
												size="small"
												label="Loan Type"
												value={filters.loanType}
												onChange={handleFilterChange("loanType")}
											>
												<MenuItem value="all">All</MenuItem>
												{loanTypeOptions.map((type) => (
													<MenuItem value={type} key={type}>
														{type}
													</MenuItem>
												))}
											</TextField>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												select
												fullWidth
												size="small"
												label="Loan Category"
												value={filters.loanCategory}
												onChange={handleFilterChange("loanCategory")}
											>
												<MenuItem value="all">All</MenuItem>
												<MenuItem value="thirdparty">Third Party</MenuItem>
												<MenuItem value="studentloan">Student Loan</MenuItem>
											</TextField>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												fullWidth
												size="small"
												label="LGA"
												value={filters.lga}
												onChange={handleFilterChange("lga")}
											/>
										</Grid>
									</Grid>
									<Stack direction="row" spacing={1.2} sx={{ mt: 1.5 }}>
										<Button
											variant="outlined"
											onClick={clearFilters}
											sx={{
												borderColor: "#64748b",
												color: "#334155",
												textTransform: "none",
												fontWeight: 700,
											}}
										>
											Clear Filters
										</Button>
										<Typography sx={{ color: "#475569", alignSelf: "center" }}>
											Showing {filteredApplicants.toLocaleString()} of {totalApplicants.toLocaleString()} total records
										</Typography>
									</Stack>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12}>
							<Card
								sx={{
									borderRadius: 4,
									background: "rgba(255,255,255,0.95)",
								}}
							>
								<CardContent>
									<Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
										Matrix Record Table
									</Typography>
									<Typography sx={{ color: "#475569", mb: 2 }}>
										Filtered records: <strong>{filteredApplicants.toLocaleString()}</strong> / {totalApplicants.toLocaleString()} total
									</Typography>
									<Box sx={{ width: "100%", overflowX: "auto" }}>
										<TableContainer component={Paper} elevation={0} sx={{ minWidth: 2200 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 800, backgroundColor: "#ecfdf5" }}>S/N</TableCell>
														{MATRIX_COLUMNS.map((column) => (
															<TableCell key={column.id} sx={{ fontWeight: 800, whiteSpace: "nowrap", backgroundColor: "#ecfdf5" }}>
																{column.label}
															</TableCell>
														))}
													</TableRow>
												</TableHead>
												<TableBody>
													{matrixRecords.length > 0 ? (
														matrixRecords.map((record) => (
															<TableRow key={`matrix-row-${record["S/N"]}`} hover>
																<TableCell sx={{ fontWeight: 700 }}>{record["S/N"]}</TableCell>
																{MATRIX_COLUMNS.map((column) => (
																	<TableCell key={`${record["S/N"]}-${column.id}`} sx={{ whiteSpace: "nowrap" }}>
																		{record[column.label]}
																	</TableCell>
																))}
															</TableRow>
														))
													) : (
														<TableRow>
															<TableCell colSpan={MATRIX_COLUMNS.length + 1}>
																<Alert severity="info">No records found in the API response.</Alert>
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</TableContainer>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12}>
							<Card
								sx={{
									borderRadius: 4,
									background: "rgba(255,255,255,0.95)",
								}}
							>
								<CardContent>
									<Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
										Quick Summary
									</Typography>
									<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
										{Object.entries(derived.loanTypeCounts).length > 0 ? (
											Object.entries(derived.loanTypeCounts)
												.sort((a, b) => b[1] - a[1])
												.map(([label, count]) => (
													<Box
														key={label}
														sx={{
															borderRadius: 999,
															px: 2,
															py: 1,
															background: "#065f46",
															color: "white",
															fontWeight: 700,
														}}
													>
														{label}: {count}
													</Box>
												))
										) : (
											<Alert severity="info">No loan type values found in the API response.</Alert>
										)}
									</Stack>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				)}
			</div>
		</div>
	);
}

export default Matrix;
